import { useEffect, useRef, useState } from 'react';

const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://vanpolattesisat.biz/#business',
  name: 'Van Polat Tesisat',
  description:
    'Van genelinde su kaçağı tespiti, tıkanıklık açma, kombi servisi, petek temizleme ve tüm tesisat işleri. 7/24 acil servis.',
  url: 'https://vanpolattesisat.biz',
  telephone: '+905559676914',
  email: 'vanpolattesisat@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Cumhuriyet Caddesi',
    addressLocality: 'Van',
    addressRegion: 'Van',
    postalCode: '65000',
    addressCountry: 'TR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 38.489117,
    longitude: 43.234412,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  priceRange: '₺₺',
  areaServed: {
    '@type': 'City',
    name: 'Van',
  },
  serviceArea: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: 38.489117,
      longitude: 43.234412,
    },
    geoRadius: '50000',
  },
  hasMap: 'https://maps.google.com/?q=Van,Türkiye',
  sameAs: ['https://www.google.com/maps?cid=Van+Polat+Tesisat'],
};

const INFO_ITEMS = [
  {
    icon: 'ri-map-pin-2-fill',
    color: 'bg-red-500',
    label: 'Hizmet Bölgesi',
    value: 'Van Merkez ve Tüm İlçeler',
    sub: 'Edremit, Erciş, Gevaş, İpekyolu, Tuşba...',
  },
  {
    icon: 'ri-time-fill',
    color: 'bg-green-500',
    label: 'Çalışma Saatleri',
    value: '7/24 Acil Servis',
    sub: 'Hafta sonu ve tatil günleri dahil',
  },
  {
    icon: 'ri-phone-fill',
    color: 'bg-orange-500',
    label: 'Hızlı Arama',
    value: '0555 967 69 14',
    sub: 'Ortalama 30 dak. içinde yanınızda',
  },
  {
    icon: 'ri-award-fill',
    color: 'bg-amber-500',
    label: 'Deneyim',
    value: '15+ Yıllık Ustabaşı',
    sub: 'Sertifikalı ve sigortalı ekip',
  },
];

export default function MapSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-12 md:py-20"
      style={{ background: 'rgb(248, 248, 246)' }}
      aria-label="Van Polat Tesisat Konum ve Hizmet Bölgesi"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <i className="ri-map-pin-2-fill text-xs"></i>
            Van Genelinde Hizmet
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Van'da Her Adrese{' '}
            <span className="text-red-500">En Hızlı Tesisat</span> Hizmeti
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
            Van merkez ve tüm ilçelerinde — su kaçağı tespiti, tıkanıklık açma ve kombi
            servisi için ortalama <strong>30 dakika</strong> içinde kapınızdayız.
          </p>
        </div>

        {/* Info cards */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {INFO_ITEMS.map((item) => (
            <div
              key={item.label}
              className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 flex flex-col gap-2"
            >
              <span className={`w-10 h-10 flex items-center justify-center ${item.color} rounded-xl shrink-0`}>
                <i className={`${item.icon} text-white text-lg`}></i>
              </span>
              <div>
                <p className="text-xs text-gray-400 font-semibold mb-0.5">{item.label}</p>
                <p className="text-sm font-extrabold text-gray-900 leading-tight">{item.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Map + CTA */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* Google Maps embed — full width on mobile, 2/3 on desktop */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-gray-100 h-64 md:h-96">
            <iframe
              title="Van Polat Tesisat — Van Tesisatçı Konum"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d192094.4579!2d43.23441185!3d38.489117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x400b29745f69a6ef%3A0x3f6c49a5d7b5dcde!2sVan%2C%20T%C3%BCrkiye!5e0!3m2!1str!2str!4v1711723200000!5m2!1str!2str"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              aria-label="Van Polat Tesisat Google Harita Konumu"
            />
          </div>

          {/* Right CTA panel */}
          <div className="flex flex-col gap-3">
            {/* Address card */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 flex items-center justify-center bg-red-500 rounded-lg">
                  <i className="ri-store-2-fill text-white text-sm"></i>
                </span>
                <p className="font-extrabold text-gray-900 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Van Polat Tesisat
                </p>
              </div>
              <address className="not-italic text-xs text-gray-600 leading-relaxed mb-4">
                <strong className="text-gray-900">Adres:</strong> Cumhuriyet Caddesi, Van Merkez<br />
                <strong className="text-gray-900">İl:</strong> Van, Türkiye — 65000<br />
                <strong className="text-gray-900">Tel:</strong>{' '}
                <a href="tel:+905076074165" className="text-red-500 font-semibold hover:underline cursor-pointer">
                  0507 607 4165
                </a>
              </address>

              <div className="space-y-2">
                <a
                  href="tel:+905076074165"
                  className="flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-sm"></i>
                  Hemen Ara
                </a>
                <a
                  href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20hizmeti%20almak%20istiyorum."
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-sm"></i>
                  WhatsApp Yaz
                </a>
                <a
                  href="https://maps.google.com/?q=Van,Türkiye"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-2-line text-sm"></i>
                  Yol Tarifi Al
                </a>
              </div>
            </div>

            {/* Badge */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-xl shrink-0">
                <i className="ri-shield-check-fill text-white text-lg"></i>
              </span>
              <div>
                <p className="text-xs font-extrabold text-gray-900">Güvenceli Hizmet</p>
                <p className="text-xs text-gray-500 mt-0.5">İşçilik garantisi & Sigortalı ekip</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
